import { useGesture } from "@use-gesture/react";
import { AnimatePresence, animate, motion, useMotionValue } from "motion/react";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { BUTTON } from "@/components/styles/variants/button";
import useBreakpoints from "@/modules/hooks/use-breakpoints";
import { colors, sizes, spacings } from "../../assets/themes";
import Block from "../block";
import Button from "../button";
import Icon from "../icon";
import Modal from "../modal/index";
import { Body16 } from "../text";

const StyledNavigationWrapper = styled.div`
  ${({ left }) => left && "left : 0"};
  ${({ right }) => right && "right : 0"};
  top: 0;
  bottom: 0;
  margin: auto 0;
  position: absolute;
  display: flex;
  align-items: center;
  z-index: 9;
  margin: ${spacings.m};
  ${
    "" /* prevent user missclick outside button and trigger the close by setting a width (size of the button) and hide the button only instead of the wrapper when page limits */
  }
  cursor: default;
  width: ${sizes.size48};
`;

const StyledGalleryHeader = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${sizes.size64};
  align-items: center;
  justify-content: flex-end;
  padding: ${spacings.s};
  z-index: 10;
  background-color: ${({ solidBackground }) => (solidBackground ? colors.body : "transparent")};
`;

const StyledNavigationButton = styled(Button.Medium)`
  background-color: ${colors.overlayDark}!important;
  * {
    color: ${colors.white};
  }
  &:hover {
    &:after {
      background-color: ${colors.overlayDark}!important;
    }
  }
`;

const StyledPagination = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`;

const StyledMotionImageWrapper = styled(motion.div)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: center;
`;

const StyledImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  pointer-events: all;
  user-select: none;
  cursor: default;
`;

const StyledGalleryWrapper = styled.div`
  height: 100vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: stretch;
  flex-direction: column;
`;

const StyledCustomComponentWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 10;
  padding-top: ${sizes.size64};
`;

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? window.innerWidth : -window.innerWidth,
    opacity: 0,
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    zIndex: 0,
    x: direction < 0 ? window.innerWidth : -window.innerWidth,
    opacity: 0,
  }),
};

const swipeConfidenceThreshold = 10_000;
const swipePower = (offset, velocity) => Math.abs(offset) * velocity;

const ImageGalleryModal = ({
  isOpen,
  onClose,
  initialIndex = 0,
  renderImagesOverlay,
  children,
}) => {
  const DOUBLE_TAP_DELAY = 300;

  const scale = useMotionValue(1);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const breakpoints = useBreakpoints();

  const animatedWrapperRef = useRef(null);
  const imageRef = useRef(null);
  const tapTimeoutRef = useRef(null);
  const lastTapRef = useRef(0);
  const isDragging = useRef(false);
  const [childs] = useState(React.Children.toArray(children));
  const [overlayHidden, setOverlayHidden] = useState(false);
  const [wrapperConstraints, setWrapperConstraints] = useState({});
  const [[page, direction], setPage] = useState([initialIndex, 0]);
  const [imageSize, setImageSize] = useState(null);

  useEffect(() => {
    setImageSize(imageRef.current?.getBoundingClientRect());
  }, [imageRef.current]);

  const getSrcFromChildrenIndex = () => childs[page]?.props?.srcSet || childs[page]?.props?.src;

  const isVideo = (src) => {
    if (!src) {
      return false;
    }
    const videoExtensions = [
      "mp4",
      "webm",
      "ogg",
      "mov",
      "avi",
      "flv",
      "mkv",
      "mpeg",
      "mpg",
      "wmv",
      "3gp",
      "m4v",
    ];
    const extension = src.split(".").pop();
    return videoExtensions.includes(extension.toLowerCase());
  };

  const isMobile = breakpoints.get({ xs: true, md: false });
  const canNext = page < childs.length - 1;
  const canPrev = page > 0;
  const isCurrentMediaVideo = isVideo(getSrcFromChildrenIndex());
  const canZoom = isMobile && !isCurrentMediaVideo;
  const canPaginate = scale.get() <= 1;
  const canDragWrapper = canPaginate;
  const canDragImage = scale.get() > 1;

  const calculateDragConstraints = (currentScale) => {
    if (!(imageSize && animatedWrapperRef.current)) {
      return { left: 0, right: 0, top: 0, bottom: 0 };
    }

    const containerWidth = animatedWrapperRef.current.offsetWidth;
    const containerHeight = animatedWrapperRef.current.offsetHeight;

    const scaledWidth = imageSize.width * currentScale;
    const scaledHeight = imageSize.height * currentScale;

    const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
    const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);

    const constraints = {
      left: -maxX,
      right: maxX,
      top: -maxY,
      bottom: maxY,
    };
    setWrapperConstraints(constraints);
    return constraints;
  };

  const paginate = (newDirection) => {
    let v = page + newDirection;
    if (v < 0) {
      v = 0;
    } else if (v > childs.length - 1) {
      v = childs.length - 1;
    }
    setPage([v, newDirection]);
    requestAnimationFrame(() => {
      setOverlayHidden(false);
      x.set(0);
      y.set(0);
      scale.set(1);
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      paginate(1);
    }
    if (e.key === "ArrowLeft") {
      paginate(-1);
    }
  };

  useEffect(() => {
    if (imageSize) {
      scale.onChange((s) => {
        requestAnimationFrame(() => {
          const { left, right, top, bottom } = calculateDragConstraints(s);
          // Ajust X constraint
          const currentX = x.get();
          if (currentX < left) animate(x, left, { duration: 0 });
          if (currentX > right) animate(x, right, { duration: 0 });

          // Ajust Y constraint
          const currentY = y.get();
          if (currentY < top) animate(y, top, { duration: 0 });
          if (currentY > bottom) animate(y, bottom, { duration: 0 });
        });
        if (s > 1) {
          setOverlayHidden(true);
        }
      });
    }
  }, [scale, imageSize]);

  useEffect(() => {
    if (isOpen) document.addEventListener("keydown", handleKeyDown);
    if (!isOpen) document.removeEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [page, isOpen]);

  useGesture(
    {
      onPinch: ({ offset: [s], memo = scale.get() }) => {
        if (canZoom) {
          scale.set(s);
        }
        return memo;
      },
    },
    {
      target: document.body,
      pinch: {
        scaleBounds: { min: 1, max: 5 },
        rubberband: true,
        from: [scale.get()],
        enabled: canZoom,
      },
    }
  );

  const handleDoubleTap = () => {
    if (!isCurrentMediaVideo) {
      setOverlayHidden((s) => !s);
      if (scale.get() !== 1) {
        animate(scale, 1);
      } else {
        animate(scale, 2);
      }
      animate(x, 0);
      animate(y, 0);
    }
  };

  const handleTap = () => {
    if (!isDragging.current && isMobile) {
      const now = Date.now();
      const isDoubleTap = now - lastTapRef.current < DOUBLE_TAP_DELAY;

      if (isDoubleTap) {
        if (tapTimeoutRef.current) {
          clearTimeout(tapTimeoutRef.current);
          tapTimeoutRef.current = null;
        }
        handleDoubleTap();
      }
      lastTapRef.current = now;
    }
  };

  useEffect(
    () => () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
    },
    []
  );

  const handleDragEnd = () => {
    setTimeout(() => {
      isDragging.current = false;
    }, 0);
  };

  const handleDragStart = () => {
    isDragging.current = true;
  };

  const handleWrapperDragEnd = (e, { offset, velocity }) => {
    const swipe = swipePower(offset.x, velocity.x);
    const currentScale = scale.get();

    if (currentScale === 1) {
      if (swipe < -swipeConfidenceThreshold && canNext) {
        paginate(1);
      } else if (swipe > swipeConfidenceThreshold && canPrev) {
        paginate(-1);
      }
    }
    handleDragEnd();
  };

  const handleChildDragEnd = (e, info) => {
    handleDragEnd();
  };

  return (
    <Modal.FullScreen
      css={`
        overflow: hidden;
        > div > div {
          padding: 0;
          background-color: ${colors.body};
        }
      `}
      hideBackdrop
      isOpen={isOpen}
      onClose={onClose}
    >
      <StyledGalleryWrapper>
        <StyledGalleryHeader solidBackground={!overlayHidden}>
          {childs.length > 1 && (
            <StyledPagination>
              <Body16 color={colors.white} strong>
                {page + 1}/{childs.length}
              </Body16>
            </StyledPagination>
          )}
          <StyledNavigationButton
            kind={BUTTON.KIND.MINIMAL}
            onClick={onClose}
            // prevent mouseDownEvent parent triggering
            onMouseDown={(e) => e.stopPropagation()}
            shape={BUTTON.SHAPE.CIRCLE}
          >
            <Icon.Large color={colors.white} name="times" />
          </StyledNavigationButton>
        </StyledGalleryHeader>

        <AnimatePresence custom={direction} initial={false} mode="wait">
          <StyledMotionImageWrapper
            animate={{
              ...variants.center,
            }}
            custom={direction}
            drag={canDragWrapper ? "x" : false}
            dragConstraints={animatedWrapperRef}
            dragElastic={1}
            exit="exit"
            initial="enter"
            key={page}
            onClick={(e) => {
              // Prevent default to avoid iOS Safari issues
              e.preventDefault();
              if (!isDragging.current) {
                handleTap();
              }
            }}
            onDragEnd={handleWrapperDragEnd}
            onDragStart={handleDragStart}
            ref={animatedWrapperRef}
            style={{ position: "relative" }}
            transition={{
              x: { ease: "easeOut", duration: 0.1 },
              opacity: { duration: 0.1 },
            }}
            variants={variants}
            whileTap={{ cursor: "grabbing" }}
          >
            {renderImagesOverlay && !overlayHidden && (
              <StyledCustomComponentWrapper onMouseDown={(e) => e.stopPropagation()}>
                {renderImagesOverlay(page)}
              </StyledCustomComponentWrapper>
            )}

            <motion.div
              drag={canDragImage}
              dragConstraints={wrapperConstraints}
              dragElastic={1}
              onDragEnd={handleChildDragEnd}
              onDragStart={handleDragStart}
              ref={imageRef}
              style={{ scale, x, y }}
            >
              {isVideo(getSrcFromChildrenIndex()) ? (
                <StyledImg
                  as={motion.video}
                  autoPlay
                  controls
                  controlsList="nodownload noremoteplayback disablePictureInPicture noplaybackrate"
                  disablePictureInPicture
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  style={{ pointerEvents: "auto" }}
                  width="100%"
                >
                  <source src={getSrcFromChildrenIndex()} type="video/mp4" />
                  Your browser does not support the video tag.
                </StyledImg>
              ) : (
                <StyledImg
                  onMouseDown={(e) => e.stopPropagation()}
                  src={getSrcFromChildrenIndex()}
                  style={isMobile ? {} : { pointerEvents: "none" }}
                />
              )}
            </motion.div>
          </StyledMotionImageWrapper>
        </AnimatePresence>

        <Block display={{ xs: "none", md: "block" }}>
          <StyledNavigationWrapper
            left
            onMouseDown={(e) => e.stopPropagation()} // prevent mouseDownEvent parent triggering
          >
            {canPrev && (
              <StyledNavigationButton
                kind={BUTTON.KIND.MINIMAL}
                onClick={() => {
                  paginate(-1);
                }}
                shape={BUTTON.SHAPE.CIRCLE}
              >
                <Icon.Large color={colors.white} name="angle-left" />
              </StyledNavigationButton>
            )}
          </StyledNavigationWrapper>
        </Block>
        <Block display={{ xs: "none", md: "block" }}>
          <StyledNavigationWrapper
            onMouseDown={(e) => e.stopPropagation()}
            right // prevent mouseDownEvent parent triggering
          >
            {canNext && (
              <StyledNavigationButton
                kind={BUTTON.KIND.MINIMAL}
                onClick={() => {
                  paginate(1);
                }}
                shape={BUTTON.SHAPE.CIRCLE}
              >
                <Icon.Large color={colors.white} name="angle-right" />
              </StyledNavigationButton>
            )}
          </StyledNavigationWrapper>
        </Block>
      </StyledGalleryWrapper>
    </Modal.FullScreen>
  );
};

export default ImageGalleryModal;
